import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem, CommandGroup } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { StatusPill } from "@/components/StatusPill";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, CalendarDays, RotateCcw, AlertTriangle, Lock, Unlock, History, Paperclip, Pencil, MessageSquare, Check, ChevronsUpDown } from "lucide-react";
import { format } from "date-fns";

const empty = {
    client_id: "", branch_id: "", technician_id: "", scheduled_date: format(new Date(), "yyyy-MM-dd"),
    scheduled_time: "08:00", service_value: 0, order_number: "", invoice_ref: "", invoice_type: "FAS",
    service_type_flag: "", instructions: "", supplies_assigned: [],
    service_type: "servicio", auditor_id: "", previous_technician: "", salesperson: "",
};

export default function AdminSchedules() {
    const [items, setItems] = useState([]);
    const [techs, setTechs] = useState([]);
    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);
    const [clientOpen, setClientOpen] = useState(false);
    const [clientSearch, setClientSearch] = useState("");
    const [branchOpen, setBranchOpen] = useState(false);
    const [branchSearch, setBranchSearch] = useState("");
    const clientListRef = React.useRef(null);
    const branchListRef = React.useRef(null);
    const [auditors, setAuditors] = useState([]);
    const [filterDate, setFilterDate] = useState(format(new Date(), "yyyy-MM-dd"));
    const [filterTech, setFilterTech] = useState("all");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null); // null = create, string = edit mode
    const [supplyInput, setSupplyInput] = useState({ product: "", quantity: 1, unit: "u", pest_target: "" });
    const [confirmReopen, setConfirmReopen] = useState(null);
    const [cancellationHistory, setCancellationHistory] = useState(null); // service to show history for
    const [uploadDocService, setUploadDocService] = useState(null); // service to attach doc to
    const [uploadDocForm, setUploadDocForm] = useState({ title: "", document_type: "constancias", file: null });
    const [uploadDocLoading, setUploadDocLoading] = useState(false);
    const [notesService, setNotesService] = useState(null);
    const [notesData, setNotesData] = useState({ client_notes: [], tech_notes: [], seller_notes: [] });
    const [notesLoading, setNotesLoading] = useState(false);

    const load = async () => {
        const params = {};
        if (filterDate) params.date = filterDate;
        if (filterTech && filterTech !== "all") params.technician_id = filterTech;
        const r = await api.get("/schedules", { params });
        setItems(r.data);
    };
    // eslint-disable-next-line
    useEffect(() => { load(); }, [filterDate, filterTech]);
    useEffect(() => {
        api.get("/technicians").then((r) => setTechs(r.data));
        api.get("/clients").then((r) => setClients(r.data));
        api.get("/auth/auditors").then((r) => setAuditors(r.data || [])).catch(() => {});
    }, []);

    // Load branches for the selected billing client (searchable branch selector)
    useEffect(() => {
        if (!form.client_id) { setBranches([]); return; }
        api.get("/branches", { params: { client_id: form.client_id } })
            .then((r) => setBranches(r.data || []))
            .catch(() => setBranches([]));
    }, [form.client_id]);

    // Keep the combobox lists scrolled to the top while filtering (avoids the
    // jarring "scroll jumps to bottom" behaviour when results are at the top).
    useEffect(() => { if (clientListRef.current) clientListRef.current.scrollTop = 0; }, [clientSearch]);
    useEffect(() => { if (branchListRef.current) branchListRef.current.scrollTop = 0; }, [branchSearch]);

    const submit = async () => {
        const isAudit = form.service_type === "auditoria";
        if (!form.client_id) return toast.error("Cliente requerido");
        if (isAudit && !form.auditor_id) return toast.error("Seleccione un auditor");
        if (!isAudit && !form.technician_id) return toast.error("Técnico requerido");
        try {
            const payload = { ...form, service_value: Number(form.service_value) || 0 };
            if (isAudit) { payload.technician_id = ""; if (!payload.service_type_flag) payload.service_type_flag = "Auditoría"; }
            else { payload.auditor_id = null; }
            if (editingId) {
                await api.put(`/schedules/${editingId}`, payload);
                toast.success("Servicio actualizado");
            } else {
                await api.post("/schedules", payload);
                toast.success(isAudit ? "Auditoría programada" : "Servicio programado");
            }
            setOpen(false); setEditingId(null); setForm(empty); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    const openNotes = async (svc) => {
        setNotesService(svc);
        setNotesLoading(true);
        try {
            const r = await api.get(`/schedules/${svc.id}/all-notes`);
            setNotesData(r.data);
        } catch { setNotesData({ client_notes: [], tech_notes: [], seller_notes: [] }); }
        finally { setNotesLoading(false); }
    };

    const startEdit = (s) => {
        setForm({
            client_id: s.client_id || "",
            branch_id: s.branch_id || "",
            technician_id: s.technician_id || "",
            scheduled_date: s.scheduled_date || format(new Date(), "yyyy-MM-dd"),
            scheduled_time: s.scheduled_time || "08:00",
            service_value: s.service_value || 0,
            order_number: s.order_number || "",
            invoice_ref: s.invoice_ref || "",
            invoice_type: s.invoice_type || "FAS",
            service_type_flag: s.service_type_flag || "",
            instructions: s.instructions || "",
            supplies_assigned: s.supplies_assigned || [],
        });
        setEditingId(s.id);
        setOpen(true);
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminar servicio?")) return;
        await api.delete(`/schedules/${id}`);
        toast.success("Eliminado"); load();
    };

    const reopen = async (id) => {
        setConfirmReopen(id);
    };

    const confirmReopenAction = async () => {
        const id = confirmReopen;
        setConfirmReopen(null);
        try {
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seed/reopen/${id}`, { method: "POST" });
            const data = await r.json();
            if (data.reopened) {
                toast.success(`Servicio reabierto (${data.reports_deleted} reporte(s) borrado(s))`);
                load();
            } else {
                toast.error("No se pudo reabrir");
            }
        } catch {
            toast.error("Error al reabrir el servicio");
        }
    };

    const toggleAllowAddProducts = async (id, current) => {
        try {
            await api.patch(`/schedules/${id}/allow-add-products`);
            toast.success(current ? "Permiso de agregar productos desactivado" : "Técnico puede agregar productos");
            load();
        } catch {
            toast.error("Error al cambiar el permiso");
        }
    };

    const submitUploadDoc = async () => {
        if (!uploadDocForm.title || !uploadDocForm.file) return toast.error("Título y archivo requeridos");
        const svc = uploadDocService;
        setUploadDocLoading(true);
        try {
            const fd = new FormData();
            fd.append("client_id", svc.client_id);
            fd.append("scheduled_service_id", svc.id);
            fd.append("document_type", uploadDocForm.document_type);
            fd.append("title", uploadDocForm.title);
            fd.append("file", uploadDocForm.file);
            await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Documento adjuntado");
            setUploadDocService(null);
            setUploadDocForm({ title: "", document_type: "constancias", file: null });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al subir el documento");
        } finally {
            setUploadDocLoading(false);
        }
    };

    const addSupply = () => {
        if (!supplyInput.product) return;
        setForm((f) => ({ ...f, supplies_assigned: [...f.supplies_assigned, supplyInput] }));
        setSupplyInput({ product: "", quantity: 1, unit: "u", pest_target: "" });
    };

    return (
        <div data-testid="admin-schedules">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-semibold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk',sans-serif" }}><CalendarDays className="w-6 h-6" /> Programación</h1>
                <Button onClick={() => { setEditingId(null); setForm({ ...empty, scheduled_date: filterDate }); setOpen(true); }} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" data-testid="admin-schedule-new">
                    <Plus className="w-4 h-4 mr-1" /> Programar servicio
                </Button>
            </div>
            <div className="flex gap-3 mb-3 flex-wrap">
                <div>
                    <Label className="text-xs">Fecha</Label>
                    <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="w-40" data-testid="admin-schedule-filter-date" />
                </div>
                <div>
                    <Label className="text-xs">Técnico</Label>
                    <Select value={filterTech} onValueChange={setFilterTech}>
                        <SelectTrigger className="w-56" data-testid="admin-schedule-filter-tech"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} – {t.full_name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <Card>
                <Table>
                    <TableHeader>
                        <TableRow><TableHead>Hora</TableHead><TableHead>Cliente</TableHead><TableHead>Técnico</TableHead><TableHead>Tipo de servicio</TableHead><TableHead>Orden</TableHead><TableHead>Valor</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Sin servicios para los filtros seleccionados</TableCell></TableRow>}
                        {items.map((s) => {
                            const c = clients.find((x) => x.id === s.client_id);
                            const t = techs.find((x) => x.id === s.technician_id);
                            return (
                                <TableRow key={s.id}>
                                    <TableCell className="font-mono">{s.scheduled_time}</TableCell>
                                    <TableCell>{c?.name || "—"}</TableCell>
                                    <TableCell>{t?.full_name || "—"}</TableCell>
                                    <TableCell className="text-sm text-zinc-600">{s.service_type_flag || <span className="text-zinc-300">—</span>}</TableCell>
                                    <TableCell className="font-mono text-sm">{s.order_number}</TableCell>
                                    <TableCell className="font-mono">${(s.service_value || 0).toFixed(2)}</TableCell>
                                    <TableCell><StatusPill status={s.status} /></TableCell>
                                    <TableCell className="text-right space-x-1">
                                        {s.start_cancellations?.length > 0 && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setCancellationHistory(s)}
                                                title={`${s.start_cancellations.length} cancelación(es) de inicio`}
                                                data-testid={`schedule-cancel-history-${s.id}`}
                                                className="relative"
                                            >
                                                <History className="w-4 h-4 text-orange-500" />
                                                <span className="absolute -top-0.5 -right-0.5 bg-orange-500 text-white text-[9px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center leading-none">
                                                    {s.start_cancellations.length}
                                                </span>
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => startEdit(s)}
                                            title="Editar servicio"
                                            data-testid={`schedule-edit-${s.id}`}
                                        >
                                            <Pencil className="w-4 h-4 text-zinc-500" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setUploadDocService(s)}
                                            title="Adjuntar documento PDF al servicio"
                                            data-testid={`schedule-attach-doc-${s.id}`}
                                        >
                                            <Paperclip className="w-4 h-4 text-blue-500" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => openNotes(s)}
                                            title="Notas del servicio"
                                            data-testid={`schedule-notes-${s.id}`}
                                        >
                                            <MessageSquare className="w-4 h-4 text-emerald-600" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => toggleAllowAddProducts(s.id, s.allow_add_products)}
                                            title={s.allow_add_products ? "Bloquear: técnico NO puede agregar productos" : "Permitir: técnico puede agregar productos"}
                                            data-testid={`schedule-allow-products-${s.id}`}
                                        >
                                            {s.allow_add_products
                                                ? <Unlock className="w-4 h-4 text-green-600" />
                                                : <Lock className="w-4 h-4 text-zinc-400" />
                                            }
                                        </Button>
                                        {s.status !== "pending" && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => reopen(s.id)}
                                                title="Reabrir servicio (volver a pendiente)"
                                                data-testid={`schedule-reopen-${s.id}`}
                                            >
                                                <RotateCcw className="w-4 h-4 text-[#FFD62B] stroke-[2.5]" />
                                            </Button>
                                        )}
                                        <Button variant="ghost" size="icon" onClick={() => remove(s.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Card>

            <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setEditingId(null); setForm(empty); } }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{editingId ? "Editar servicio" : "Programar servicio"}</DialogTitle></DialogHeader>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2">
                            <Label>Modalidad</Label>
                            <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
                                <SelectTrigger data-testid="sched-form-modalidad"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="servicio">Servicio (Técnico)</SelectItem>
                                    <SelectItem value="auditoria">Auditoría (Auditor)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Cliente</Label>
                            <Popover open={clientOpen} onOpenChange={(o) => { setClientOpen(o); if (!o) setClientSearch(""); }}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="sched-form-client">
                                        <span className="truncate">{form.client_id ? (clients.find((c) => c.id === form.client_id)?.name || "Seleccione") : "Seleccione"}</span>
                                        <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                                    <Command>
                                        <CommandInput placeholder="Buscar cliente..." value={clientSearch} onValueChange={setClientSearch} data-testid="sched-client-search" />
                                        <CommandList ref={clientListRef}>
                                            <CommandEmpty>Sin resultados</CommandEmpty>
                                            <CommandGroup>
                                                {clients.map((c) => (
                                                    <CommandItem key={c.id} value={c.name} onSelect={() => { setForm((f) => ({ ...f, client_id: c.id, branch_id: "" })); setClientOpen(false); setClientSearch(""); }} data-testid={`sched-client-opt-${c.id}`}>
                                                        <Check className={`mr-2 h-4 w-4 ${form.client_id === c.id ? "opacity-100" : "opacity-0"}`} />
                                                        {c.name}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                        {form.service_type === "auditoria" ? (
                            <div>
                                <Label>Auditor</Label>
                                <Select value={form.auditor_id} onValueChange={(v) => setForm((f) => ({ ...f, auditor_id: v }))}>
                                    <SelectTrigger data-testid="sched-form-auditor"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                                    <SelectContent>{auditors.map((a) => <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <div>
                                <Label>Técnico</Label>
                                <Select value={form.technician_id} onValueChange={(v) => setForm((f) => ({ ...f, technician_id: v }))}>
                                    <SelectTrigger data-testid="sched-form-tech"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                                    <SelectContent>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} – {t.full_name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        )}
                        {branches.length > 0 && (
                            <div className="sm:col-span-2">
                                <Label>Sucursal</Label>
                                <Popover open={branchOpen} onOpenChange={(o) => { setBranchOpen(o); if (!o) setBranchSearch(""); }}>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="sched-form-branch">
                                            <span className="truncate">{form.branch_id ? (branches.find((b) => b.id === form.branch_id)?.name || "Seleccione sucursal") : "— Sin sucursal específica —"}</span>
                                            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                                        <Command>
                                            <CommandInput placeholder="Buscar sucursal..." value={branchSearch} onValueChange={setBranchSearch} data-testid="sched-branch-search" />
                                            <CommandList ref={branchListRef}>
                                                <CommandEmpty>Sin resultados</CommandEmpty>
                                                <CommandGroup>
                                                    <CommandItem value="sin sucursal especifica" onSelect={() => { setForm((f) => ({ ...f, branch_id: "" })); setBranchOpen(false); setBranchSearch(""); }} data-testid="sched-branch-opt-none">
                                                        <Check className={`mr-2 h-4 w-4 ${!form.branch_id ? "opacity-100" : "opacity-0"}`} />
                                                        — Sin sucursal específica —
                                                    </CommandItem>
                                                    {branches.map((b) => (
                                                        <CommandItem key={b.id} value={`${b.name} ${b.address || ""}`} onSelect={() => { setForm((f) => ({ ...f, branch_id: b.id })); setBranchOpen(false); setBranchSearch(""); }} data-testid={`sched-branch-opt-${b.id}`}>
                                                            <Check className={`mr-2 h-4 w-4 shrink-0 ${form.branch_id === b.id ? "opacity-100" : "opacity-0"}`} />
                                                            <span className="truncate">{b.name}{b.address ? ` · ${b.address}` : ""}</span>
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        )}
                        <div><Label>Fecha</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setForm((f) => ({ ...f, scheduled_date: e.target.value }))} data-testid="sched-form-date" /></div>
                        <div><Label>Hora</Label><Input type="time" value={form.scheduled_time} onChange={(e) => setForm((f) => ({ ...f, scheduled_time: e.target.value }))} data-testid="sched-form-time" /></div>
                        <div><Label>Orden</Label><Input value={form.order_number} onChange={(e) => setForm((f) => ({ ...f, order_number: e.target.value }))} placeholder="60252-675587" /></div>
                        <div><Label>Factura ref.</Label><Input value={form.invoice_ref} onChange={(e) => setForm((f) => ({ ...f, invoice_ref: e.target.value }))} placeholder="CCF-25DS000C27943" /></div>
                        <div>
                            <Label>Tipo factura</Label>
                            <Select value={form.invoice_type} onValueChange={(v) => setForm((f) => ({ ...f, invoice_type: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{["FAS","CCF","ND","NC_NDA"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>Valor</Label><Input type="number" step="0.01" value={form.service_value} onChange={(e) => setForm((f) => ({ ...f, service_value: e.target.value }))} /></div>
                        <div className="sm:col-span-2"><Label>Tipo de servicio</Label><Input value={form.service_type_flag} onChange={(e) => setForm((f) => ({ ...f, service_type_flag: e.target.value }))} placeholder="Ej: Servicio de Control General de Plagas" data-testid="sched-form-service-type" /></div>
                        {form.service_type === "auditoria" && (
                            <>
                                <div><Label>Técnico anterior</Label><Input value={form.previous_technician} onChange={(e) => setForm((f) => ({ ...f, previous_technician: e.target.value }))} placeholder="Nombre del técnico" data-testid="sched-form-prev-tech" /></div>
                                <div><Label>Vendedor</Label><Input value={form.salesperson} onChange={(e) => setForm((f) => ({ ...f, salesperson: e.target.value }))} placeholder="Nombre del vendedor" data-testid="sched-form-salesperson" /></div>
                            </>
                        )}
                        <div className="sm:col-span-2"><Label>Instrucciones</Label><Textarea value={form.instructions} onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))} className="min-h-20" /></div>
                        {form.service_type !== "auditoria" && (
                        <div className="sm:col-span-2">
                            <Label>Insumos asignados</Label>
                            {form.supplies_assigned.length > 0 && (
                                <div className="flex flex-wrap gap-1 my-2">
                                    {form.supplies_assigned.map((s, i) => (
                                        <Badge key={i} variant="outline" className="font-mono">
                                            {s.product} – {s.quantity} {s.unit}{s.pest_target ? ` · ${s.pest_target}` : ""}
                                            <button className="ml-1" onClick={() => setForm((f) => ({ ...f, supplies_assigned: f.supplies_assigned.filter((_, idx) => idx !== i) }))}>×</button>
                                        </Badge>
                                    ))}
                                </div>
                            )}
                            <div className="grid grid-cols-[2fr_1fr_1fr_auto] gap-2">
                                <Input placeholder="Producto" value={supplyInput.product} onChange={(e) => setSupplyInput((s) => ({ ...s, product: e.target.value }))} />
                                <Input type="number" step="0.1" value={supplyInput.quantity} onChange={(e) => setSupplyInput((s) => ({ ...s, quantity: e.target.value }))} />
                                <Input placeholder="Unidad" value={supplyInput.unit} onChange={(e) => setSupplyInput((s) => ({ ...s, unit: e.target.value }))} />
                                <Button type="button" variant="outline" onClick={addSupply}>+</Button>
                            </div>
                            <Input placeholder="Plaga objetivo (ej: cucarachas, roedores)" value={supplyInput.pest_target} onChange={(e) => setSupplyInput((s) => ({ ...s, pest_target: e.target.value }))} className="mt-2" />
                        </div>
                        )}
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => { setOpen(false); setEditingId(null); setForm(empty); }}>Cancelar</Button><Button onClick={submit} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" data-testid="sched-form-submit">{editingId ? "Guardar cambios" : "Crear"}</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm reopen dialog */}
            <Dialog open={!!confirmReopen} onOpenChange={(o) => { if (!o) setConfirmReopen(null); }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-[#FFD62B]" />
                            Reabrir servicio
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        El reporte y los archivos asociados serán eliminados, y el servicio volverá a estado <strong>Pendiente</strong>. Esta acción no se puede deshacer.
                    </p>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setConfirmReopen(null)} data-testid="reopen-cancel">Cancelar</Button>
                        <Button onClick={confirmReopenAction} className="bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold" data-testid="reopen-confirm">Sí, reabrir</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancellation history dialog */}
            <Dialog open={!!cancellationHistory} onOpenChange={(o) => { if (!o) setCancellationHistory(null); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History className="w-5 h-5 text-orange-500" />
                            Historial de cancelaciones de inicio
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            {cancellationHistory && (
                                <>Servicio: <strong>{clients.find(c => c.id === cancellationHistory.client_id)?.name || cancellationHistory.client_id}</strong> — {cancellationHistory.scheduled_date} {cancellationHistory.scheduled_time}</>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                        {cancellationHistory?.start_cancellations?.length > 0 ? (
                            cancellationHistory.start_cancellations.map((c, i) => (
                                <div key={i} className="rounded-lg border bg-orange-50 border-orange-100 px-3 py-2.5 text-sm">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="font-semibold text-orange-800">{c.cancelled_by_name}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {c.cancelled_at ? new Date(c.cancelled_at).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" }) : "—"}
                                        </span>
                                    </div>
                                    {c.entry_time_was && (
                                        <p className="text-xs text-orange-600 mt-0.5">
                                            Inicio registrado: {new Date(c.entry_time_was).toLocaleTimeString("es-SV", { hour: "2-digit", minute: "2-digit" })}
                                            {c.gps_lat_was && ` · GPS: ${Number(c.gps_lat_was).toFixed(4)}, ${Number(c.gps_lng_was).toFixed(4)}`}
                                        </p>
                                    )}
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">Sin cancelaciones registradas</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCancellationHistory(null)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Upload document dialog */}
            <Dialog open={!!uploadDocService} onOpenChange={(o) => { if (!o) { setUploadDocService(null); setUploadDocForm({ title: "", document_type: "other", file: null }); } }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Paperclip className="w-5 h-5 text-blue-500" />
                            Adjuntar documento al servicio
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            {uploadDocService && (
                                <>Servicio: <strong>{clients.find(c => c.id === uploadDocService.client_id)?.name || "—"}</strong> — {uploadDocService.scheduled_date} {uploadDocService.scheduled_time}</>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Título del documento</Label>
                            <Input
                                placeholder="Ej: Certificado de fumigación Mayo 2025"
                                value={uploadDocForm.title}
                                onChange={(e) => setUploadDocForm((f) => ({ ...f, title: e.target.value }))}
                                data-testid="doc-upload-title"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Tipo de documento</Label>
                            <Select value={uploadDocForm.document_type} onValueChange={(v) => setUploadDocForm((f) => ({ ...f, document_type: v }))}>
                                <SelectTrigger data-testid="doc-upload-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="legal">Documentos Legales</SelectItem>
                                    <SelectItem value="products">Fichas de Productos</SelectItem>
                                    <SelectItem value="certifications">Contactos y Certificaciones</SelectItem>
                                    <SelectItem value="constancias">Protocolos de Servicio</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Archivo PDF</Label>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                className="block w-full text-sm mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:bg-zinc-100 file:text-zinc-700 hover:file:bg-zinc-200 cursor-pointer"
                                onChange={(e) => setUploadDocForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
                                data-testid="doc-upload-file"
                            />
                            {uploadDocForm.file && (
                                <p className="text-xs text-muted-foreground mt-1">{uploadDocForm.file.name} ({(uploadDocForm.file.size / 1024).toFixed(0)} KB)</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter className="gap-2 mt-2">
                        <Button variant="outline" onClick={() => { setUploadDocService(null); setUploadDocForm({ title: "", document_type: "other", file: null }); }} disabled={uploadDocLoading}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={submitUploadDoc}
                            disabled={uploadDocLoading || !uploadDocForm.title || !uploadDocForm.file}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid="doc-upload-submit"
                        >
                            {uploadDocLoading ? "Subiendo..." : "Subir documento"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Notes dialog */}
            <Dialog open={!!notesService} onOpenChange={(o) => { if (!o) setNotesService(null); }}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-emerald-600" />
                            Notas del servicio
                        </DialogTitle>
                        <DialogDescription>
                            {notesService && `${clients.find(c => c.id === notesService.client_id)?.name || "—"} · ${notesService.scheduled_time} · ${notesService.scheduled_date}`}
                        </DialogDescription>
                    </DialogHeader>
                    {notesLoading ? (
                        <div className="py-8 text-center text-muted-foreground text-sm">Cargando notas...</div>
                    ) : (
                        <div className="space-y-4">
                            {/* Client notes */}
                            <div>
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Notas del cliente</h3>
                                {notesData.client_notes.length === 0 ? (
                                    <p className="text-xs text-muted-foreground italic">Sin notas del cliente</p>
                                ) : notesData.client_notes.map(n => (
                                    <div key={n.id} className="rounded-lg border p-3 mb-2 bg-blue-50/50">
                                        <p className="text-sm whitespace-pre-wrap">{n.notes}</p>
                                        <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[10px] text-muted-foreground">
                                            {n.signature_url && <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200 px-1.5 py-0">Firmado</Badge>}
                                            {n.latitude && (
                                                <a href={`https://www.google.com/maps?q=${n.latitude},${n.longitude}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                                                    Ubicación
                                                </a>
                                            )}
                                            <span className="ml-auto">{new Date(n.created_at).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" })}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Tech notes */}
                            <div>
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Notas del técnico</h3>
                                {notesData.tech_notes.length === 0 ? (
                                    <p className="text-xs text-muted-foreground italic">Sin notas del técnico</p>
                                ) : notesData.tech_notes.map(n => (
                                    <div key={n.id} className="rounded-lg border p-3 mb-2 bg-amber-50/50">
                                        <p className="text-sm whitespace-pre-wrap">{n.notes}</p>
                                        <div className="text-[10px] text-muted-foreground mt-1.5">
                                            {n.technician_name} · {new Date(n.created_at).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" })}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Seller notes */}
                            <div>
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Notas al vendedor</h3>
                                {(!notesData.seller_notes || notesData.seller_notes.length === 0) ? (
                                    <p className="text-xs text-muted-foreground italic">Sin notas al vendedor</p>
                                ) : notesData.seller_notes.map(n => (
                                    <div key={n.id} className="rounded-lg border p-3 mb-2 bg-yellow-50/60" data-testid={`sched-seller-note-${n.id}`}>
                                        <p className="text-sm whitespace-pre-wrap">{n.notes}</p>
                                        <div className="text-[10px] text-muted-foreground mt-1.5">
                                            {n.technician_name} · {new Date(n.created_at).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
